import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Boxes,
  ExternalLink,
  FileText,
  KeyRound,
  Plus,
  Rocket,
  RotateCw,
  Trash2,
  UserRound,
} from 'lucide-react';
import type { GenieacsStatus } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import {
  PageHeader,
  Card,
  Button,
  ErrorNotice,
  Field,
  Input,
  Pill,
  Spinner,
} from '../../components/ui';
import { CheckIcon, ObjectList } from '../settings/PlatformCards';

interface ServiceRow {
  name: string;
  running: boolean;
  status: string;
}

interface StandUpOutcome {
  steps: string[];
  alreadyRunning: boolean;
}

/**
 * The local GenieACS deployment's home — and now its controls.
 *
 * The page grew from a placeholder into management in the order the roadmap
 * promised: the containers as docker sees them, restarts (one or the stack in
 * dependency order), and each container's recent logs. Every privileged move
 * runs through the `simpleacs-genieacs` root helper behind sudo, and a
 * restart is never a casual button — it costs seconds of CWMP outage for
 * every informing CPE, and the page says so where the button is.
 */
export function GenieacsPage() {
  const tasks = useTasks();
  const [logsFor, setLogsFor] = useState<'genieacs' | 'mongo' | null>(null);

  const services = useQuery({
    queryKey: ['genieacs', 'services'],
    queryFn: () => api.get<{ items: ServiceRow[]; adapter: 'shell' | 'dry-run' }>('/genieacs/services'),
    refetchInterval: 30_000,
  });

  /**
   * A restart runs in the tasks tray, not behind a spinner: it is a
   * seconds-long CWMP outage the operator has already decided on, and the
   * outcome must not depend on this page staying open. Same treatment as the
   * stand-up below.
   */
  const restart = (target: 'genieacs' | 'mongo' | 'all') =>
    tasks.start({
      label: target === 'all' ? 'Restart the GenieACS stack' : `Restart ${target}`,
      run: () => api.post('/genieacs/restart', { target }),
      done: () => `Restarted ${target === 'all' ? 'the whole stack' : target}.`,
      href: '/infrastructure/genieacs',
      invalidate: [['genieacs', 'services']],
    });

  const logs = useQuery({
    queryKey: ['genieacs', 'logs', logsFor],
    queryFn: () => api.get<{ lines: string[] }>(`/genieacs/logs/${logsFor}`),
    enabled: logsFor !== null,
  });

  // The ACS's own health — stock provisions, presets, firmware files — shown
  // inside the Deployment card rather than as a card of its own (operator's
  // call), with the UI link promoted to a button at the top of the page.
  const health = useQuery({
    queryKey: ['infra', 'genieacs'],
    queryFn: () => api.get<GenieacsStatus>('/infra/genieacs'),
    refetchInterval: 60_000,
  });
  const g = health.data;

  return (
    <>
      <PageHeader
        title="GenieACS"
        description="The local TR-069 server SimpleACS drives — its containers, restarts and logs."
        actions={
          g?.uiUrl ? (
            <a href={g.uiUrl} target="_blank" rel="noreferrer">
              <Button variant="primary" icon={<ExternalLink className="size-4" />}>
                Open the UI
              </Button>
            </a>
          ) : undefined
        }
      />
      <div className="space-y-5">
        {!services.isLoading && (
          <StandUpCard
            absent={!services.error && !(services.data?.items ?? []).some((s) => s.running)}
          />
        )}
        <Card
          title={
            <span className="flex items-center gap-2">
              <Boxes className="size-4 text-muted" />
              Deployment
              {services.data?.adapter === 'dry-run' && <Pill tone="neutral">dry-run</Pill>}
            </span>
          }
          action={
            <Button
              size="sm"
              variant="secondary"
              icon={<RotateCw className="size-3.5" />}
              onClick={() => restart('all')}
            >
              Restart the stack
            </Button>
          }
        >
          {services.isLoading ? (
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          ) : services.error ? (
            <div className="p-5">
              <ErrorNotice error={services.error} />
            </div>
          ) : (
            <>
              <div className="divide-y divide-line">
                {(services.data?.items ?? []).map((svc) => {
                  const target = svc.name === 'genieacs-mongo' ? 'mongo' : 'genieacs';
                  return (
                    <div key={svc.name} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                      <span className="flex items-center gap-3">
                        <Pill state tone={svc.running ? 'success' : 'danger'} dot>
                          {svc.running ? 'running' : 'stopped'}
                        </Pill>
                        <span className="font-mono text-sm text-ink">{svc.name}</span>
                        <span className="text-xs text-faint">{svc.status}</span>
                      </span>
                      <span className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<FileText className="size-3.5" />}
                          onClick={() => setLogsFor(logsFor === target ? null : target)}
                        >
                          {logsFor === target ? 'Hide logs' : 'Logs'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<RotateCw className="size-3.5" />}
                          onClick={() => restart(target)}
                        >
                          Restart
                        </Button>
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="border-t border-line px-5 py-2.5 text-[11px] text-faint">
                A restart is a seconds-long outage for every informing CPE — devices retry on
                their own, but a fleet mid-provision is a reason to wait.
              </p>
              {g && (
                <div className="space-y-3 border-t border-line p-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <Pill state tone={g.healthy ? 'success' : 'warn'} dot>
                      {g.healthy ? 'healthy' : 'check'}
                    </Pill>
                    {(['bootstrap', 'inform', 'default'] as const).map((name) => (
                      <span
                        key={name}
                        className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-sm"
                      >
                        <CheckIcon ok={g.stock[name]} />
                        <span className="font-mono text-ink">{name}</span>
                      </span>
                    ))}
                    <span className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-sm text-muted">
                      {g.fileCount} firmware file{g.fileCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <ObjectList
                      label={`Provisions (${g.provisions.length})`}
                      names={g.provisions.map((p) => p.name)}
                    />
                    <ObjectList
                      label={`Presets (${g.presets.length})`}
                      names={g.presets.map((p) => p.name)}
                    />
                  </div>
                  <p className="text-[11px] text-faint">
                    These write per-device Connection Request credentials and the hourly refresh.
                    SimpleACS verifies they exist — it never overwrites them.
                  </p>
                </div>
              )}
            </>
          )}
        </Card>

        {logsFor && (
          <Card title={`Logs — ${logsFor === 'mongo' ? 'genieacs-mongo' : 'genieacs'} (last 200 lines)`}>
            {logs.isLoading ? (
              <div className="flex justify-center p-8">
                <Spinner />
              </div>
            ) : (
              <pre className="max-h-96 overflow-auto p-4 font-mono text-xs leading-relaxed text-muted">
                {(logs.data?.lines ?? []).join('\n') || 'Nothing logged.'}
              </pre>
            )}
          </Card>
        )}

        <UiAccountsCard />
      </div>
    </>
  );
}

/**
 * Standing the stack up from here, so a box whose GenieACS is missing or
 * stopped is repaired without a shell.
 *
 * `absent` is "nothing is running" — no containers at all, or all of them
 * stopped — and that is when the card speaks up: a paragraph on what the
 * stand-up installs and a primary button. With the stack running the same
 * card shrinks to one line and a re-run link, because the helper's `up` is
 * idempotent (it re-installs the auth extension, checks the ./ext volume and
 * restarts nothing) and there is no reason to hide a free repair.
 *
 * The work runs through the tasks tray rather than a spinner on the button:
 * a fresh box pulls two images first, which is minutes, and the operator
 * should be free to leave this page while it happens.
 */
function StandUpCard({ absent }: { absent: boolean }) {
  const tasks = useTasks();

  const start = () =>
    tasks.start<StandUpOutcome>({
      label: 'Stand up GenieACS',
      run: () => api.post<StandUpOutcome>('/genieacs/up'),
      done: (r) =>
        r.alreadyRunning
          ? 'GenieACS was already running — the auth extension and ./ext volume were checked, nothing was restarted.'
          : `GenieACS is up — ${r.steps.filter((s) => !s.startsWith('  ')).length} steps ran.`,
      href: '/infrastructure/genieacs',
      invalidate: [
        ['genieacs', 'services'],
        ['genieacs', 'users'],
        ['infra', 'genieacs'],
      ],
    });

  if (!absent) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface px-5 py-3">
        <span className="text-xs text-muted">
          The stack is up. The stand-up can be re-run at any time — it keeps the existing compose
          file and UI secret, re-installs the auth extension and restarts nothing.
        </span>
        <button
          type="button"
          className="text-xs text-link hover:underline"
          onClick={start}
        >
          Re-run stand-up (idempotent)
        </button>
      </div>
    );
  }

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Rocket className="size-4 text-muted" />
          Stand up GenieACS
        </span>
      }
      action={
        <Button
          size="sm"
          variant="primary"
          icon={<Rocket className="size-3.5" />}
          onClick={start}
        >
          Stand up GenieACS
        </Button>
      }
    >
      <p className="px-5 py-4 text-sm text-muted">
        Nothing is running. Standing up installs the shipped docker-compose file into
        /opt/genieacs, generates the UI secret once, installs the SimpleACS auth extension,
        mounts its ./ext volume and runs <span className="font-mono">docker compose up -d</span>{' '}
        — the same steps a deploy runs. It costs nothing on a box that already has the stack:
        an existing compose file and secret are kept and nothing running is restarted. A first
        stand-up pulls two images, so allow a few minutes; progress lands in the tasks tray.
      </p>
    </Card>
  );
}

/**
 * GenieACS's OWN UI accounts (the interface on port 3000) — not SimpleACS
 * users. Passwords are hashed server-side with GenieACS's exact parameters;
 * SimpleACS never stores or shows one back, so "reset" is the only recovery.
 */
function UiAccountsCard() {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ username: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['genieacs', 'users'],
    queryFn: () => api.get<{ items: Array<{ username: string; roles: string }> }>('/genieacs/users'),
  });

  const remove = useMutation({
    mutationFn: (username: string) =>
      api.delete(`/genieacs/users/${encodeURIComponent(username)}`),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['genieacs', 'users'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not delete'),
  });

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <UserRound className="size-4 text-muted" />
          UI accounts
        </span>
      }
      action={
        <Button
          size="sm"
          variant="secondary"
          icon={<Plus className="size-3.5" />}
          onClick={() => setDialog({ username: null })}
        >
          New account
        </Button>
      }
    >
      {users.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : users.error ? (
        <div className="p-5">
          <ErrorNotice error={users.error} />
        </div>
      ) : (
        <>
          {error && (
            <div className="px-5 pt-4">
              <ErrorNotice error={error} />
            </div>
          )}
          <div className="divide-y divide-line">
            {(users.data?.items ?? []).map((u) => (
              <div key={u.username} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <span className="flex items-center gap-3">
                  <span className="font-mono text-sm text-ink">{u.username}</span>
                  <Pill tone="info">{u.roles || 'no role'}</Pill>
                </span>
                <span className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<KeyRound className="size-3.5" />}
                    onClick={() => setDialog({ username: u.username })}
                  >
                    Reset password
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Delete this UI account"
                    icon={<Trash2 className="size-3.5" />}
                    loading={remove.isPending && remove.variables === u.username}
                    onClick={() => {
                      // An unlabelled trash icon one gap from "Reset password",
                      // and not recoverable: the password is hashed and never
                      // stored, so a deleted account cannot be put back as it
                      // was — only recreated with a new one.
                      if (
                        confirm(
                          `Delete the GenieACS UI account "${u.username}"? Its password cannot be recovered — recreating it means a new one.`,
                        )
                      ) {
                        remove.mutate(u.username);
                      }
                    }}
                  />
                </span>
              </div>
            ))}
          </div>
          <p className="border-t border-line px-5 py-2.5 text-[11px] text-faint">
            These sign in to GenieACS's own interface on port 3000. SimpleACS hashes the password
            the way GenieACS does and never stores it — resetting is the only recovery. The last
            account cannot be deleted.
          </p>
        </>
      )}
      {dialog && (
        <UiAccountDialog
          username={dialog.username}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await queryClient.invalidateQueries({ queryKey: ['genieacs', 'users'] });
          }}
        />
      )}
    </Card>
  );
}

function UiAccountDialog({
  username,
  onClose,
  onSaved,
}: {
  username: string | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState({ username: username ?? '', password: '' });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.put('/genieacs/users', {
        username: form.username.trim(),
        password: form.password,
        roles: 'admin',
      }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    save.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-sm rounded-xl border border-line bg-surface shadow-2xl"
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            {username ? `Reset password — ${username}` : 'New UI account'}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            For GenieACS's own interface. The password is hashed and forgotten.
          </p>
        </header>
        <div className="grid gap-4 p-5">
          <Field label="Username">
            <Input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="admin"
              disabled={username !== null}
              required
              autoFocus={username === null}
            />
          </Field>
          <Field label="Password">
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="At least 8 characters"
              minLength={8}
              required
              autoFocus={username !== null}
            />
          </Field>
        </div>
        {error && <p className="px-5 pb-1 text-xs font-medium text-danger">{error}</p>}
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {username ? 'Reset' : 'Create'}
          </Button>
        </footer>
      </form>
    </div>
  );
}
